import React from 'react'
import * as R from 'remeda'

import {
  AnalysisCommentBlock,
  useGenerateSingleComment,
} from '~/components/features/AnalysisCommentBlock'
import { DisplayToggleButton } from '~/components/features/DisplayToggleButton'
import { ShareDropDownMenuButton } from '~/components/features/ShareDropDownMenuButton'
import {
  SortButton,
  type SortButtonProps,
} from '~/components/features/SortButton'
import { Table } from '~/components/features/Table'
import { TableContainer } from '~/components/features/TableContainer'
import { useLogEvent } from '~/hooks/useLogEvent'
import { type Question } from '~/hooks/useSurveyQuery'
import {
  type DisplayColumnItem,
  exists,
  getDiffKey,
  type GroupAndAll,
  QuestionOp,
  serializeTableToText,
} from '~/utils'

import { useExpandText } from '../hooks/useExpandText'
import { useMatrixDisplayRowItems } from '../hooks/useMatrixDisplayRowItems'
import { useMatrixTableColumns } from '../hooks/useMatrixTableColumns'
import { useMatrixTableInfo } from '../hooks/useMatrixTableInfo'
import { useSubQuestions } from '../hooks/useSubQuestions'
import {
  useAtomContext,
  useAtomValueContext,
  useCommentContext,
  useSavedCommentContext,
  useSetAtomContext,
} from '../providers/AtomsProvider'
import { useCommentGenerationContext } from '../providers/CommentGenerationProvider'
import { useFigureInfo } from '../providers/CreateFigureInfoProvider'
import { useRestoringParams } from '../providers/CreateRestoringParamsProvider'
import {
  type DisplayColumnKey,
  type DisplayRowId,
  type MatrixSortColumnKey,
} from '../utils'

import { EditQuestionsDiffButton } from './EditQuestionsDiffButton'
import styles from './MatrixTable.css'

export type MatrixTableProps = {
  group: GroupAndAll
  question: Question
}

export const MatrixTable = ({ group, question }: MatrixTableProps) => {
  const questionsDiffsMap = useAtomValueContext('questionsDiffsMap')
  const subQuestions = useSubQuestions(question)
  const sortButtonItems = React.useMemo<
    SortButtonProps<MatrixSortColumnKey>['items']
  >(() => {
    const questionsDiffs = questionsDiffsMap[question.question_id] ?? []

    return R.concat(
      subQuestions.map((subQuestion) => ({
        columnKey: subQuestion.question_id,
        label: QuestionOp.toText(subQuestion),
      })),
      questionsDiffs.map((questionsDiff) => {
        const columnKey = getDiffKey(questionsDiff)

        return { columnKey, label: columnKey }
      }),
    )
  }, [question.question_id, questionsDiffsMap, subQuestions])
  const [sortValue, setSortValue] = useAtomContext('matrixSortValue')

  const displayRowItems = useMatrixDisplayRowItems(question)
  const setHiddenRowIds = useSetAtomContext('hiddenRowIds')
  const handleHiddenRowChange = React.useCallback(
    (hiddenIds: DisplayRowId[]) => {
      setHiddenRowIds(
        R.piped(
          R.difference(
            displayRowItems.flatMap(({ subItems }) =>
              subItems.map(R.prop('id')),
            ),
          ),
          R.concat(hiddenIds),
        ),
      )
    },
    [displayRowItems, setHiddenRowIds],
  )

  const [hiddenColumnKeys, setHiddenColumnKeys] =
    useAtomContext('hiddenColumnKeys')
  const displayColumnItems = React.useMemo<
    DisplayColumnItem<DisplayColumnKey>[]
  >(
    () =>
      subQuestions.map((subQuestion) => ({
        columnKey: subQuestion.question_id,
        label: QuestionOp.toText(subQuestion),
        isVisible: !hiddenColumnKeys.includes(subQuestion.question_id),
      })),
    [hiddenColumnKeys, subQuestions],
  )
  const handleHiddenColumnChange = React.useCallback(
    (hiddenColumnKeys: DisplayColumnKey[]) => {
      setHiddenColumnKeys(
        R.piped(
          R.difference(subQuestions.map(R.prop('question_id'))),
          R.concat(hiddenColumnKeys),
        ),
      )
    },
    [setHiddenColumnKeys, subQuestions],
  )

  const columns = useMatrixTableColumns(question)
  const figureInfo = useFigureInfo('group_matrix')
  const restoreKey = `matrix-${question.question_id}-${group}`
  const restoringParams = useRestoringParams(restoreKey)
  const tableInfo = useMatrixTableInfo(question, group, {
    answerText: styles.cellAnswerTextColumn,
    answerTextHeader: styles.cellAnswerTextHeaderColumn,
  })

  const { handleTextExpandedChange, isTextExpanded, textLineCount } =
    useExpandText(tableInfo)

  const image = (
    <Table
      columns={columns}
      tableInfo={tableInfo}
      textLineCount={textLineCount}
    />
  )

  const { generate, isGenerating } = useGenerateSingleComment()
  const { outputType, surveyId } = useCommentGenerationContext()

  const [comment, setComment] = useCommentContext(restoreKey)
  const [savedComment, setSavedComment] = useSavedCommentContext(restoreKey)

  const logEvent = useLogEvent()
  const handleAutoGenerate = React.useCallback(async () => {
    const result = await generate({
      brandName: '',
      // TODO: filterDescription は暫定で空文字（対象条件ブロックは出力されない）。実フィルター条件を渡す
      filterDescription: '',
      filterPatternIndex: 0,
      outputType,
      segmentMode: 'none',
      surveyId,
      tabulationData: serializeTableToText(columns, tableInfo),
    })
    if (exists(result)) {
      setComment(result)
      logEvent('Generate AI comment', { withSuffix: true })
    }
  }, [generate, logEvent, outputType, surveyId, columns, tableInfo, setComment])

  return (
    <>
      <AnalysisCommentBlock
        isGenerating={isGenerating}
        savedComment={savedComment}
        saveDocumentImage={{
          commentReadOnly: true,
          figureInfo,
          image,
          initialComment: comment,
          onSaved: (saved) => setSavedComment(saved),
          restoringParams,
        }}
        value={comment}
        onAutoGenerate={handleAutoGenerate}
        onCommentChange={setComment}
      />
      <TableContainer
        header={
          <>
            <SortButton
              items={sortButtonItems}
              value={sortValue}
              onChange={setSortValue}
            />

            <DisplayToggleButton
              columnItems={displayColumnItems}
              isTextExpanded={isTextExpanded}
              isVisibleExpandTextCheckbox={true}
              rowItems={displayRowItems}
              onHiddenColumnChange={handleHiddenColumnChange}
              onHiddenRowChange={handleHiddenRowChange}
              onTextExpandedChange={handleTextExpandedChange}
            />

            <ShareDropDownMenuButton
              copyToClipboard={{
                columns,
                figureInfo,
                restoringParams,
                tableInfo,
              }}
              downloadAsPng={{ image }}
              getSharedUrl={{ restoringParams }}
              saveDocumentImage={{
                commentReadOnly: true,
                figureInfo,
                image,
                initialComment: comment,
                onSaved: (saved) => setSavedComment(saved),
                restoringParams,
              }}
            />

            <EditQuestionsDiffButton question={question} />
          </>
        }
        id={group !== 'all' ? restoreKey : undefined}
      >
        <Table
          columns={columns}
          stickyColumns={1}
          tableInfo={tableInfo}
          textLineCount={textLineCount}
        />
      </TableContainer>
    </>
  )
}
